import { subdomainPathRedirects, subdomainRedirects } from '@data/redirects';
import axios from 'axios';

const IS_SERVER = typeof window === 'undefined';

export type SubdomainType = 'shopGroup' | 'shop' | 'advisor' | 'team';

export interface SubdomainData {
  id: string;
  type: SubdomainType;
  url: string;
  domain: string;
  sort: number;
  shopDomain: string;
  userId: string | null;
  chimeId: string | null;
  shopGroupImage: string;
  useExternalShopWebsite?: boolean;
}

export interface SubdomainInfo {
  baseUrl: string;
  subdomain: string | null;
  shopSubdomain: string | null;
  type: SubdomainType | null;
  domain: string;
  advisorId?: string | null;
  redirectUrl?: string | null;
  useExternalWebsite?: boolean;
}

export default function getURL(path: string, config) {
  const baseURL = IS_SERVER ? config.siteURL : window.location.origin;
  return baseURL + path;
}

export function extractMlsIdAndListingIdFromUrl(slug: string[]) {
  const slugParts = slug[slug.length - 1]!.split('-');
  if (slugParts.length < 2) {
    throw new Error('Invalid listing slug: ' + slug);
  }

  const listingId = decodeURIComponent(slugParts.pop()!).replace(/\$/g, '-');
  const mlsId = slugParts.pop()!;
  return { listingId, mlsId };
}

/**
 * Returns the base URL for the current request. If a valid shop subdomain is
 * detected, the base URL will include it (i.e. barrie.evrealestate.com). Otherwise,
 * the base URL will be returned (i.e. evrealestate.com).
 */
export async function getBaseUrlAndSubdomain(originHost: string): Promise<SubdomainInfo> {
  // Add protocol if not present
  if (!originHost.includes('http')) {
    if (originHost.includes('localhost')) {
      originHost = 'http://' + originHost;
    } else {
      originHost = 'https://' + originHost;
    }
  }

  const url = new URL(originHost);
  const hostWithoutWWW = url.host.replace('www.', '');
  let baseUrl = hostWithoutWWW;
  let subdomain = extractSubdomain(hostWithoutWWW);
  let type: SubdomainType | null = null;
  let shopSubdomain: string | null = null;
  let advisorId: string | undefined;
  let redirectUrl: string | undefined;
  let useExternalWebsite: boolean = false;

  if (subdomain) {
    // We hardcode the domain to evrealestate.com because our services/data have the domain filled in and must match
    const fullHost = `${subdomain}.evrealestate.com`;
    const shopServiceUrl = process.env.NEXT_PUBLIC_SHOP_SERVICE_URL;
    if (!shopServiceUrl) {
      throw new Error('NEXT_PUBLIC_SHOP_SERVICE_URL is not defined');
    }

    try {
      const url = `${shopServiceUrl}/v1/places/domain?domain=${fullHost}`;
      const res = await axios.get<SubdomainData[]>(url);
      if (res) {
        const validDomain = res.data[0];

        if (validDomain) {
          baseUrl = baseUrl.replace('://', `://${subdomain}.`);
          type = validDomain.type;
          useExternalWebsite = validDomain.useExternalShopWebsite || false;

          if (validDomain.shopDomain) {
            shopSubdomain = validDomain.shopDomain.split('.')[0]!;
          }
          if (validDomain.id) {
            advisorId = validDomain.id;
          }
        } else {
          subdomain = null;
        }
      } else {
        subdomain = null;
      }
    } catch (err) {
      console.error('Error fetching subdomain data', err);
      // If the request fails, we can assume that the subdomain is not valid
      // and we can return the base URL without the subdomain
      if (!subdomain!.includes('digital-exp')) {
        baseUrl = baseUrl.replace(`${subdomain}.`, '');
      }
      redirectUrl = `${url.protocol}//${baseUrl}`;
      subdomain = null;
    }
  }

  return {
    baseUrl: baseUrl,
    domain: extractDomain(baseUrl, subdomain),
    subdomain: subdomain,
    shopSubdomain: shopSubdomain,
    type,
    advisorId: advisorId,
    redirectUrl: redirectUrl,
    useExternalWebsite,
  };
}

export const extractDomain = (
  url: string,
  subdomain?: string | null,
  isUserPreferences = false,
): string => {
  // Add https if not present
  if (!url.includes('http')) {
    if (url.includes('localhost')) {
      url = 'http://' + url;
    } else {
      url = 'https://' + url;
    }
  }

  const parsedUrl = new URL(url);
  const parts = parsedUrl.host.replace('www.', '').split('.'); // Remove www from the domain
  if (url.includes('localhost')) {
    return isUserPreferences ? parsedUrl.hostname : parts.slice(-1).join(''); // Fixes user preferences not working on localhost
  } else if (parts.length < 2) {
    throw new Error('Invalid URL: ' + url);
  }

  const start = subdomain ? 1 : 0;
  return parts.slice(start).join('.'); // Join the last two parts to get the domain
};

export const extractSubdomain = (host?: string | null) => {
  let subdomain: string | null = null;
  if (!host && typeof window !== 'undefined') {
    // On client side, get the host from window
    host = window.location.host;
  }

  // Remove protocol and www from host
  if (host && host.includes('http')) {
    host = host.split('://')[1];
  }
  // Remove www from host
  if (host && host.includes('www.')) {
    host = host.replace('www.', '');
  }
  // Remove Non-Prod subdomain, e.g. digital-exp
  // This handles URLs like 'subdomain.digital-exp.evipscloud-dev.com' by
  // removing 'digital-exp.' so it's consistent with standard domains
  if (host && host.includes('digital-exp.')) {
    host = host.replace('digital-exp.', '');
  }

  if (host) {
    // Determine minimum parts needed to have a subdomain:
    // - localhost: 2 parts (e.g., 'subdomain.localhost:3000')
    // - vercel.app: 4 parts (e.g., 'subdomain.myapp.vercel.app')
    // - evipscloud and all other domains: 3 parts (e.g., 'subdomain.domain.com')
    const requiredPartsForSubdomain = host.includes('localhost')
      ? 2
      : host.includes('vercel.app')
        ? 4
        : 3;

    const parts = host!.split('.');
    subdomain =
      parts.length < requiredPartsForSubdomain
        ? null
        : host.includes('.')
          ? host.split('.')[0]!
          : null;
  }

  // if (host && host.includes('.') && !host.includes('vercel')) {
  //   const tmpHost = host.split('.');
  //   const shopName = tmpHost[0];
  //   // Check if Host is having subdomain or not
  //   if (tmpHost.length > 2 && shopName && !shopName.includes('localhost')) {
  //     // Valid shops
  //     subdomain = shopName;
  //   }
  // }
  return subdomain;
};

export const getEntityDomain = () => {
  if (typeof window === 'undefined') return null;
  let host = window.location.host;
  const domainName = host.split('.')[0];
  return `${domainName}.evrealestate.com`;
};

export function escapeXml(text: string): string {
  return text?.replace(/[<>&"']/g, (match) => {
    switch (match) {
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '&':
        return '&amp;';
      case '"':
        return '&quot;';
      case "'":
        return '&apos;';
      default:
        return match;
    }
  });
}

export const getServerSideAbsoluteUrl = (context: any, removeSubdomain = false) => {
  const protocol = context?.req?.headers['x-forwarded-proto'] || 'http';
  let host = context?.req?.headers?.['host'];
  if (removeSubdomain && host) {
    host = extractDomain(host);
    if (host.includes('evipscloud-uat.com') || host.includes('evipscloud-dev.com')) {
      host = `digital-exp.${host}`;
    }
  }
  const path = context?.resolvedUrl;
  return protocol && host && path ? `${protocol}://${host}${path}` : null;
};
